import React, { useState } from 'react';
import { Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/useAuth';
import { AuthAPI } from '../api/authApi';
import styles from './login.module.scss';
import useToast from '../../../shared/hooks/useToast';
import AuthSplitLayout from '../components/AuthSplitLayout';
import PinInput from '../components/PinInput';
import {
  ADMIN_PIN_LENGTH,
  ADMIN_PIN_PATTERN,
} from '../../admins/utils/adminUsername';

// Poruka posle "zaboravljen PIN" je uvek ista: bek ne otkriva da li nalog
// postoji niti da li je super admin (samo on dobija mejl).
const FORGOT_PIN_SENT =
  'Ako nalog postoji i ima pravo na reset PIN-a, poslali smo mejl sa uputstvom.';

const Login = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const from = location.state?.from?.pathname || '/terapije';

  // Prikaz u desnom panelu: prijava ili "zaboravljen PIN". Forma za reset se
  // pojavljuje umesto prijave (bez promene rute), uz usmereni fade.
  const [view, setView] = useState('login');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [pinResetKey, setPinResetKey] = useState(0);

  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const goForgot = () => {
    setError('');
    setForgotError('');
    setForgotSent(false);
    setForgotIdentifier(username);
    setView('forgot');
  };

  const goLogin = () => {
    setForgotError('');
    setForgotSent(false);
    setPin('');
    setView('login');
  };

  const handleLogin = async (e) => {
    e?.preventDefault?.();

    if (!username.trim() || !ADMIN_PIN_PATTERN.test(pin)) {
      setError(`Unesite korisničko ime i PIN od ${ADMIN_PIN_LENGTH} cifara.`);
      return;
    }

    setError('');
    setLoading(true);

    const {
      data,
      success,
      message: loginMessage,
      code,
    } = await AuthAPI.login({ username, pin });
    // Posle 5 pogresnih PIN-ova bek privremeno blokira prijavu za taj nalog.
    const loginError =
      code === 'TOO_MANY_LOGIN_ATTEMPTS'
        ? loginMessage
        : !success || !data || !data?._id || !data?.token
          ? 'Neuspešna prijava. Proverite korisničko ime i PIN.'
          : null;

    if (loginError) {
      setError(loginError);
      setPin('');
      setPinResetKey((key) => key + 1);
    } else {
      const { token, ...rest } = data;
      const userObject = { ...rest };

      localStorage.setItem('user', JSON.stringify(userObject));
      localStorage.setItem('token', token);

      login(userObject, token);
      toast.success('Uspešna prijava!');
      navigate(from, { replace: true });
    }

    setLoading(false);
  };

  const handleForgot = async (e) => {
    e?.preventDefault?.();

    if (!forgotIdentifier.trim()) {
      setForgotError('Unesite korisničko ime ili email.');
      return;
    }

    setForgotError('');
    setForgotLoading(true);

    // Odgovor je uvek isti (vidi FORGOT_PIN_SENT); gresku prikazujemo samo ako
    // zahtev uopste nije stigao do beka.
    const { ok, status } = await AuthAPI.forgotPin({
      identifier: forgotIdentifier,
    });

    setForgotLoading(false);
    if (!ok && !status) {
      setForgotError('Zahtev nije poslat. Proverite internet vezu.');
      return;
    }
    setForgotSent(true);
  };

  return (
    <AuthSplitLayout>
      {/* key={view} re-montira sadrzaj pa se animacija pokrene pri svakoj
          promeni: reset ulazi sa desna, prijava sa leva. */}
      <div
        key={view}
        className={view === 'forgot' ? styles.enterRight : styles.enterLeft}
      >
        {view === 'login' ? (
          <>
            <h1 className={styles.login__title}>Dobrodošli nazad</h1>
            <p className={styles.login__subtitle}>
              Unesite korisničko ime i PIN za pristup nalogu.
            </p>

            <form className={styles.login__form} onSubmit={handleLogin}>
              <div className={styles.login__field}>
                <label htmlFor="username">Korisničko ime</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="ime.prezime"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div className={styles.login__field}>
                <span id="pin-label" className={styles.login__label}>
                  PIN
                </span>
                <PinInput
                  labelId="pin-label"
                  onChange={setPin}
                  resetKey={pinResetKey}
                />
              </div>

              <div className={styles.login__row}>
                <button
                  type="button"
                  className={styles.login__forgot}
                  onClick={goForgot}
                >
                  Zaboravili ste PIN?
                </button>
              </div>

              {error && <div className={styles.login__error}>{error}</div>}

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                className={styles.login__submit}
              >
                Prijavi se
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className={styles.login__title}>Zaboravili ste PIN?</h1>
            <p className={styles.login__subtitle}>
              Unesite korisničko ime ili email vašeg naloga i poslaćemo link za
              novi PIN.
            </p>

            <form className={styles.login__form} onSubmit={handleForgot}>
              <div className={styles.login__field}>
                <label htmlFor="forgotIdentifier">
                  Korisničko ime ili email
                </label>
                <input
                  id="forgotIdentifier"
                  name="forgotIdentifier"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="ime.prezime ili email"
                  value={forgotIdentifier}
                  onChange={(e) => setForgotIdentifier(e.target.value)}
                />
              </div>

              {forgotError && (
                <div className={styles.login__error}>{forgotError}</div>
              )}

              {forgotSent && !forgotError && (
                <div className={styles.login__success}>{FORGOT_PIN_SENT}</div>
              )}

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={forgotLoading}
                className={styles.login__submit}
              >
                {forgotSent ? 'Pošalji ponovo' : 'Pošalji link'}
              </Button>

              <button
                type="button"
                className={styles.login__back}
                onClick={goLogin}
              >
                <ArrowLeftOutlined />
                Nazad na prijavu
              </button>
            </form>
          </>
        )}
      </div>
    </AuthSplitLayout>
  );
};

export default Login;
